/**
 * useAbrirTrasLogin — abre sola la ventana del Plano Google Maps pt un segundo
 * después de un login normal, a quien tiene la oferta activa.
 *
 * El login (`pages/Login.jsx`) manda a Dispositivos con una marca en el `state`
 * del historial (`TRAS_LOGIN_STATE`, la hora del login). Aquí, si la oferta
 * está activa y esa marca no se ha usado ya, se espera un segundo —que vea la
 * página y la tarjeta antes de taparla— y se abre la ventana.
 *
 * La marca usada se apunta en `sessionStorage`. Sin eso volvería a abrirse sola
 * al cerrarla: cerrar es «atrás» en el historial, y la entrada a la que se
 * vuelve es la del login, con la marca todavía puesta. Lo mismo al recargar.
 *
 * El origen es su propio CTA (`AUTO_LOGIN.ctaId`): si de ahí sale un alta, se
 * la lleva la apertura automática y no un botón que nadie pulsó. No tiene
 * click que medir (nadie pulsa nada); sus aperturas están en PostHog
 * (`oferta_pt_ventana_abierta` con ese origen).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAbrirVentana } from './useAnuncioPt';

export const TRAS_LOGIN_STATE = 'ofertaPtTrasLogin';
export const AUTO_LOGIN = { ctaId: 'ofertapt.login.auto' };
export const ESPERA_MS = 1000;

const USADA_KEY = 'mytapstar.ofertaPt.trasLoginUsada';

function yaUsada(marca) {
  try {
    return window.sessionStorage.getItem(USADA_KEY) === String(marca);
  } catch {
    return false;
  }
}

function apuntarUsada(marca) {
  try { window.sessionStorage.setItem(USADA_KEY, String(marca)); } catch { /* modo privado */ }
}

export default function useAbrirTrasLogin(activa) {
  const location = useLocation();
  const abrirVentana = useAbrirVentana();
  // El temporizador llama a la versión más reciente (la de la URL de ahora).
  const abrir = useRef(abrirVentana);
  abrir.current = abrirVentana;
  const marca = location.state?.[TRAS_LOGIN_STATE];

  useEffect(() => {
    if (!activa || !marca || yaUsada(marca)) return undefined;
    const id = setTimeout(() => {
      apuntarUsada(marca);
      abrir.current(AUTO_LOGIN.ctaId);
    }, ESPERA_MS);
    return () => clearTimeout(id);
  }, [activa, marca]);
}
